// Live Firestore reads (onSnapshot, serverTimestamps: 'estimate') and the two writes the app makes.
import { useQuery } from "@tanstack/react-query";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  type SnapshotOptions,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { CONV, EVENT, FN_WA_REPLY, FN_WA_RETRY, MSG, PATH, SENDER, THREAD_LIMIT } from "@/lib/fields";
import { db, functions } from "@/lib/firebase";
import { capUsage } from "@/model/cap";
import { overviewCounts } from "@/model/overview";
import { peopleFromData } from "@/model/people";
import { nextBatch, retryQueue, runRows } from "@/model/runs";
import { toConfig, toConversation, toLedgerRow, toMs, toRecipient, toRun, toThreadMessage } from "./convert";
import { liveQuery } from "./live";
import type { Conversation, LedgerRow, Recipient, Run, ThreadMessage, WaConfig } from "./types";

const SNAP: SnapshotOptions = { serverTimestamps: "estimate" };
const E = EVENT.key;

function liveCollection<T>(key: string, path: string, map: (id: string, d: Record<string, unknown>) => T) {
  return liveQuery<T[]>(["fs", key], (next, fail) =>
    onSnapshot(collection(db, path), (s) => next(s.docs.map((d) => map(d.id, d.data(SNAP)))), fail),
  );
}

export const ledgerQuery = liveCollection<LedgerRow>("ledger", PATH.ledger(E), toLedgerRow);
export const recipientsQuery = liveCollection<Recipient>("recipients", PATH.recipients(E), toRecipient);
export const runsQuery = liveCollection<Run>("runs", PATH.runs(E), toRun);
export const optoutsQuery = liveCollection<string>("optouts", PATH.optouts, (id) => id);
export const conversationsQuery = liveQuery<Conversation[]>(["fs", "conversations"], (next, fail) =>
  onSnapshot(
    // All of this event's threads (the number is shared across events). array-contains alone needs no
    // composite index; ordering happens client-side in the model.
    query(collection(db, PATH.conversations), where(CONV.events, "array-contains", E)),
    (s) => next(s.docs.map((d) => toConversation(d.id, d.data(SNAP)))),
    fail,
  ),
);
export const configQuery = liveQuery<WaConfig | null>(["fs", "config"], (next, fail) =>
  onSnapshot(doc(db, PATH.config(E)), (s) => next(toConfig(s.exists() ? s.data(SNAP) : undefined)), fail),
);
/** Sender heartbeat: last live cycle (ms), or null before the first one. */
export const senderQuery = liveQuery<number | null>(["fs", "sender"], (next, fail) =>
  onSnapshot(doc(db, PATH.sender(E)), (s) => next(s.exists() ? toMs(s.data(SNAP)[SENDER.lastCycleAt]) : null), fail),
);

export function threadQuery(phone: string) {
  return liveQuery<ThreadMessage[]>(["fs", "thread", phone], (next, fail) =>
    onSnapshot(
      query(collection(db, PATH.thread(phone)), orderBy(MSG.at), limit(THREAD_LIMIT)),
      (s) => next(s.docs.map((d) => toThreadMessage(d.id, d.data(SNAP)))),
      fail,
    ),
  );
}

export function useThread(phone: string | null) {
  return useQuery({ ...threadQuery(phone ?? "-"), enabled: !!phone });
}

/** A clock for countdowns and "x ago". */
export function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}

/** Everything the screens need, joined client-side by the pure model functions. */
export function useAppData() {
  const ledger = useQuery(ledgerQuery);
  const recipients = useQuery(recipientsQuery);
  const conversations = useQuery(conversationsQuery);
  const runs = useQuery(runsQuery);
  const config = useQuery(configQuery);
  const optouts = useQuery(optoutsQuery);
  const sender = useQuery(senderQuery);
  const now = useNow(60_000);
  const all = [ledger, recipients, conversations, runs, config, optouts, sender];
  const error = all.find((q) => q.error)?.error ?? null;
  const ready = all.every((q) => q.isSuccess);

  const model = useMemo(() => {
    if (!ready) return null;
    const people = peopleFromData({
      recipients: recipients.data!,
      ledger: ledger.data!,
      conversations: conversations.data!,
      optouts: new Set(optouts.data!),
      eventKey: E,
      publishedAt: config.data?.publishedAt ?? null,
    });
    const cfg = config.data ?? null;
    const cap = capUsage(ledger.data!, cfg?.dailyCap ?? 0, now);
    return {
      people,
      ledger: ledger.data!,
      runs: runs.data!,
      runRows: runRows(runs.data!, ledger.data!, now),
      config: cfg,
      cap,
      counts: overviewCounts(people),
      next: nextBatch(people, cap),
      retry: retryQueue(ledger.data!, runs.data!, sender.data ?? null, now),
    };
  }, [ready, ledger.data, recipients.data, conversations.data, runs.data, config.data, optouts.data, sender.data, now]);

  return { data: model, error, ready };
}
export type AppData = NonNullable<ReturnType<typeof useAppData>["data"]>;

// ---------------------------------------------------------------- writes (unread reset + the wa_reply / wa_retry callables)

/** wa_conversations/{phone}.unread = 0 when a thread is opened (as the old inbox does). */
export function markRead(phone: string) {
  return updateDoc(doc(db, PATH.conversation(phone)), { [CONV.unread]: 0 });
}

type ReplyIn = { phone: string; text: string } | { kind: "reaction"; phone: string; message_id: string; emoji: string };
const waReply = httpsCallable<ReplyIn, { wamid: string }>(functions, FN_WA_REPLY);
export function sendReply(phone: string, text: string) {
  return waReply({ phone, text });
}

/** Release over-limit rows; the sender sends them first in its next cycle. Sends nothing itself. */
const waRetry = httpsCallable<{ keys: string[] }, { released: number; skipped: number }>(functions, FN_WA_RETRY);
export function retryOverLimit(keys: string[]) {
  return waRetry({ keys });
}

/** React to one of their messages through the same callable; emoji "" removes our reaction. */
export function sendReaction(phone: string, messageId: string, emoji: string) {
  return waReply({ kind: "reaction", phone, message_id: messageId, emoji });
}
